import { useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { APCMStateBadge } from "./APCMStateBadge"
import { StateTimeline } from "./StateTimeline"
import { BlockersPanel } from "./pipeline/BlockersPanel"
import { ExternalLink, Loader2, UserMinus, UserPlus } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import {
  disenrollPatient,
  reenrollPatient,
  type DisenrollReason,
} from "../api/apcm-ops.api"
import type {
  PatientAPCMStateHistory,
  WorkflowStatus,
} from "../types/apcm-ops.types"
import type { PatientResponse } from "@/lib/types"
import { PatientOutreachPanel } from "@/features/outreach"

interface PatientDetailDrawerProps {
  open: boolean
  onClose: () => void
  patientId: string | null
  patient?: PatientResponse
  history: PatientAPCMStateHistory | null
  activeWorkflow: WorkflowStatus | null
  orgId?: string | null
  onRefresh?: () => void
}

const REASON_LABELS: Record<DisenrollReason, string> = {
  deceased: "Deceased",
  moved: "Moved out of service area",
  patient_request: "Patient request",
  lost_to_follow_up: "Lost to follow-up",
  insurance_changed: "Insurance changed",
  other: "Other",
}

export function PatientDetailDrawer({
  open,
  onClose,
  patientId,
  patient,
  history,
  activeWorkflow,
  orgId,
  onRefresh,
}: PatientDetailDrawerProps) {
  const { toast } = useToast()
  const [disenrollOpen, setDisenrollOpen] = useState(false)
  const [reenrollOpen, setReenrollOpen] = useState(false)
  const [reason, setReason] = useState<DisenrollReason>("lost_to_follow_up")
  const [deceasedDate, setDeceasedDate] = useState("")
  const [note, setNote] = useState("")
  const [reenrollNote, setReenrollNote] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const patientName = patient
    ? `${patient.first_name} ${patient.last_name}`
    : patientId?.slice(0, 8) || ""

  const currentState = history?.states?.[0]?.current_state
  const isDisenrolled = currentState === "DISENROLLED"

  const resetDisenrollForm = () => {
    setReason("lost_to_follow_up")
    setDeceasedDate("")
    setNote("")
  }

  const handleDisenroll = async () => {
    if (!patientId || !orgId) return
    if (reason === "deceased" && !deceasedDate) {
      toast({ title: "Date of death required", description: "Please enter the date of death.", variant: "destructive" })
      return
    }
    setSubmitting(true)
    try {
      await disenrollPatient(patientId, {
        organization_id: orgId,
        reason,
        deceased_date: reason === "deceased" ? deceasedDate : undefined,
        note: note.trim() || undefined,
      })
      toast({ title: "Patient disenrolled", description: `${patientName} removed from APCM.` })
      setDisenrollOpen(false)
      resetDisenrollForm()
      onRefresh?.()
    } catch (err: any) {
      toast({
        title: "Disenroll failed",
        description: err?.response?.data?.detail || err?.message || "Unknown error",
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleReenroll = async () => {
    if (!patientId || !orgId) return
    setSubmitting(true)
    try {
      await reenrollPatient(patientId, {
        organization_id: orgId,
        note: reenrollNote.trim() || undefined,
      })
      toast({ title: "Patient re-enrolled", description: "They will go through consent again before billing resumes." })
      setReenrollOpen(false)
      setReenrollNote("")
      onRefresh?.()
    } catch (err: any) {
      toast({
        title: "Re-enroll failed",
        description: err?.response?.data?.detail || err?.message || "Unknown error",
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:w-[500px] sm:max-w-[500px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span>{patientName}</span>
            {patientId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 ml-auto"
                onClick={() => window.open(`/patients/${patientId}`, "_blank")}
                title="Open patient page"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            )}
          </SheetTitle>
          <div className="flex items-center gap-2">
            {currentState && <APCMStateBadge state={currentState} />}
            {patient?.phone_number && (
              <span className="text-sm text-muted-foreground font-mono">{patient.phone_number}</span>
            )}
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Active Workflow */}
          {activeWorkflow && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Active Workflow</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="font-medium">{activeWorkflow.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mode</span>
                  <span className="font-mono text-xs">{activeWorkflow.execution_mode}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ID</span>
                  <span className="font-mono text-xs">{activeWorkflow.workflow_id.slice(0, 12)}...</span>
                </div>
                {activeWorkflow.tasks && activeWorkflow.tasks.length > 0 && (
                  <div className="mt-2">
                    <span className="text-xs text-muted-foreground">Tasks ({activeWorkflow.tasks.length})</span>
                    {activeWorkflow.tasks.map((task: any, i: number) => (
                      <div key={i} className="flex justify-between text-xs mt-1">
                        <span>{task.agent_name || `Task ${i + 1}`}</span>
                        <span className="text-muted-foreground">{task.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Outreach: pauses, contact preference, book / cancel call */}
          {patientId && orgId && !isDisenrolled && (
            <PatientOutreachPanel
              patientId={patientId}
              patientName={patientName}
              onChanged={onRefresh}
            />
          )}

          {/* Blockers checklist */}
          {patientId && orgId && <BlockersPanel patientId={patientId} orgId={orgId} />}

          {/* Admin actions */}
          {patientId && orgId && (
            <div className="flex flex-wrap gap-2">
              {isDisenrolled ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReenrollOpen(true)}
                  className="gap-2"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Re-enroll
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDisenrollOpen(true)}
                  className="gap-2 text-destructive hover:text-destructive"
                >
                  <UserMinus className="h-3.5 w-3.5" />
                  Disenroll
                </Button>
              )}
            </div>
          )}

          {/* State Timeline */}
          <div>
            <h3 className="text-sm font-medium mb-3">State History</h3>
            {history ? (
              history.states.length > 0 ? (
                <StateTimeline states={history.states} />
              ) : (
                <p className="text-sm text-muted-foreground">No state history found</p>
              )
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </SheetContent>

      {/* Disenroll dialog */}
      <Dialog open={disenrollOpen} onOpenChange={(o) => !o && !submitting && setDisenrollOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disenroll {patientName}</DialogTitle>
            <DialogDescription>
              This stops billing automation for this patient after the disenroll month.
              Their state will change to DISENROLLED.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="disenroll-reason">Reason</Label>
              <Select value={reason} onValueChange={(v) => setReason(v as DisenrollReason)}>
                <SelectTrigger id="disenroll-reason" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(REASON_LABELS) as DisenrollReason[]).map((r) => (
                    <SelectItem key={r} value={r}>{REASON_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reason === "deceased" && (
              <div>
                <Label htmlFor="deceased-date">Date of death</Label>
                <Input
                  id="deceased-date"
                  type="date"
                  value={deceasedDate}
                  onChange={(e) => setDeceasedDate(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used so billing stops precisely at this month, not the month you entered this.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="disenroll-note">Note (optional)</Label>
              <Textarea
                id="disenroll-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1"
                placeholder="e.g. family confirmed via phone 4/23"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisenrollOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisenroll} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disenroll"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-enroll dialog */}
      <Dialog open={reenrollOpen} onOpenChange={(o) => !o && !submitting && setReenrollOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-enroll {patientName}</DialogTitle>
            <DialogDescription>
              This moves the patient back to CONSENT_NEEDED. They'll go through fresh consent
              before billing resumes — gap months stay unbilled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="reenroll-note">Note (optional)</Label>
              <Textarea
                id="reenroll-note"
                value={reenrollNote}
                onChange={(e) => setReenrollNote(e.target.value)}
                className="mt-1"
                placeholder="e.g. patient called back asking to resume"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReenrollOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleReenroll} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Re-enroll"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  )
}
